import { Directive, HostBinding, Input, OnInit } from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ControlValueAccessor } from '@angular/forms';
import { MuiToggleValue } from './mui-toggle-value';

@Directive()
export abstract class ToggleComponentBase implements ControlValueAccessor, OnInit {
  @Input()
  @HostBinding('attr.id')
  get id(): string {
    return this._id;
  }

  set id(value: string) {
    this._id = value || this._uid;
  }

  @Input()
  get required(): boolean | null {
    return this._required;
  }

  set required(value: boolean | null) {
    this._required = coerceBooleanProperty(value);
  }

  @Input()
  @HostBinding('class.is--disabled')
  get disabled(): boolean | null {
    return this._disabled;
  }

  set disabled(value: boolean | null) {
    this._disabled = coerceBooleanProperty(value);
  }

  @HostBinding('class.is--checked')
  checked = true;

  @HostBinding('class.is--indeterminate')
  indeterminate = false;

  @Input() name: string | null = null;

  protected _id = '';
  protected _required: boolean | null = null;
  protected _disabled: boolean | null = null;
  protected abstract _uid: string;

  ngOnInit(): void {
    // Ensure the setter is called once
    /* eslint-disable-next-line no-self-assign */
    this.id = this.id;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  propagateChange = (_: MuiToggleValue): void => {};

  writeValue(value: MuiToggleValue): void {
    this.setInternalValue(value);
  }

  registerOnChange(fn: never): void {
    this.propagateChange = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  registerOnTouched(_: never): void {}

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  protected onChange(event: Event): void {
    if (this.disabled) {
      return;
    }

    const newValue = (event.target as HTMLInputElement).checked;
    this.setInternalValue(newValue);
    this.propagateChange(newValue);
  }

  protected setInternalValue(value: MuiToggleValue): void {
    if (value === 'indeterminate') {
      this.checked = false;
      this.indeterminate = true;
    } else {
      this.indeterminate = false;
      this.checked = value;
    }
  }
}
